import { useMemo } from 'react';

import { useMembersOnlyInformation } from './useMembersOnlyInformation';
import type { AlgoliaProductHit } from '~/types/algolia';
import { SigningTypes } from '~/utilities/constants';
import type { Price, Signing } from '~/utilities/graphql/codegen';

interface ParamType {
  signings?: Signing[];
  product?: AlgoliaProductHit;
  personalisedPrice?: Price | number | null;
}

interface ReturnType {
  signingLabel?: string | null;
  signings?: Signing[];
}

const RESTRICTED_SIGNING_TYPES = [SigningTypes.MANUAL];

const filterSignings = (signings?: Signing[]) =>
  signings
    ?.filter(s => !RESTRICTED_SIGNING_TYPES.includes(s?.type as SigningTypes))
    .map(s => {
      const updatedSigning = { ...s };

      if (
        updatedSigning?.type === SigningTypes.AUTOMATIC_DISCOUNTGROUP &&
        updatedSigning?.alternativeLabel
      ) {
        updatedSigning.label = updatedSigning.alternativeLabel;
      }

      return updatedSigning;
    });

export const useMembersOnlySignings = ({
  signings,
  product,
  personalisedPrice,
}: ParamType): ReturnType => {
  const activeSigning = product?.multiSigningJson?.[0];
  const personalisedPriceValue = product?.personalisedPriceValue;

  const { updatePersonalisedSignings, updateSigningForGuests, membersOnlyPriceEnabled } =
    useMembersOnlyInformation({
      personalisedPrice,
      styleVariantPersonalisedPrice: personalisedPriceValue,
    });

  return useMemo(() => {
    let signingLabel: string | null | undefined = activeSigning?.label;
    let updatedSignings: Signing[] | undefined = signings;

    if (!membersOnlyPriceEnabled) {
      updatedSignings = signings?.filter(s => !s?.membersOnlySigning);

      if (activeSigning?.membersOnlySigning) {
        signingLabel = null;
      }

      return {
        signingLabel,
        signings: updatedSignings,
      };
    }

    if (updateSigningForGuests && activeSigning?.membersOnlySigning) {
      signingLabel = activeSigning?.alternativeLabel || '';
    }

    if (updatePersonalisedSignings) {
      updatedSignings = filterSignings(signings);
    }

    return {
      signingLabel,
      signings: updatedSignings,
    };
  }, [
    activeSigning?.alternativeLabel,
    activeSigning?.label,
    activeSigning?.membersOnlySigning,
    membersOnlyPriceEnabled,
    signings,
    updatePersonalisedSignings,
    updateSigningForGuests,
  ]);
};
